import { useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import LeftSidePopupLayout from '../../../../component/popup/modal-layout'
import XCloseIcon from '../../../../component/icons/x-close'
import Style from './style.module.css'
import { useApiCall } from '../../../../component/custom-hooks/api-call'
import Swal from 'sweetalert2'
import "react-datepicker/dist/react-datepicker.css";
import { GlobalContext } from '../../../../context/Context'

// Validation schema
const validationSchema = yup.object().shape({
    userId: yup
        .array()
        .min(1, 'Please select at least one user')
        .required('Please select at least one user'),
    reason: yup
        .string()
        .required('Reason is required'),
    department: yup.string().nullable(),
    designation: yup.string().nullable(),
    employee_type: yup.string().nullable(),
    reporting_authority: yup.string().nullable(),
    shift: yup.string().nullable(),
    atLeastOne: yup.mixed().test(
        'at-least-one-field',
        'Please fill at least one of Department, Designation, Employee Type, Reporting Authority, or Shift',
        function () {
            const { department, designation, employee_type, reporting_authority, shift } = this.parent;
            return Boolean(department || designation || employee_type || reporting_authority || shift);
        }
    )
});

const EmployeeUpdateBulk = ({ onClose = () => { }, data, onChange = () => { } }) => {
    const { state } = useContext(GlobalContext);
    const apiCall = useApiCall();
    const [departmentList, setDepartmentList] = useState([]);
    const [deptUserList, setDeptUserList] = useState([]);
    const [shiftList, setShiftList] = useState({});

    const formik = useFormik({
        initialValues: {
            userId: [],
            department: "",
            designation: "",
            employee_type: "",
            reporting_authority: "",
            shift: "",
            reason: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            // Create payload only with filled values
            const payload = {
                employee_id: values?.userId,
                department: values?.department,
                designation: values?.designation,
                reportingauthority: values?.reporting_authority,
                currentshift: values?.shift,
                emptype: values?.employee_type,
                reason: values?.reason,
            };

            // Remove null/empty/undefined fields
            Object.keys(payload).forEach(
                key => (payload[key] === null || payload[key] === '' ||
                    (Array.isArray(payload[key]) && payload[key].length === 0)) && delete payload[key]
            );

            try {
                const resp = await apiCall({
                    method: 'post',
                    url: `change-bulk-employee-data`,
                    data: JSON.stringify(payload),
                });

                Swal.fire(
                    'Submitted!',
                    resp?.message,
                    'success'
                );

                formik.resetForm();
                popupClosed();
                onChange();
            } catch (error) {
                console.warn("Error : ", error);
            }
        }
    });

    const popupClosed = () => {
        onClose();
    }

    const getDropDownList = () => {
        try {
            const deptList = state?.getDropdowns?.departmentList || [];
            const activeList = state?.getDropdowns?.activeEmployeeList || [];
            const shifts = state?.getDropdowns?.shiftList || {};

            setDepartmentList(deptList);
            setUserOptions(activeList);
            setShiftList(shifts);

            const preselectedUsers = (activeList || []).filter(user =>
                data.includes(user.emp_id)
            );
            
            // Fill both UI and formik
            setSelectedUsers(preselectedUsers);
            formik.setFieldValue("userId", preselectedUsers.map(u => u.emp_id));
        } catch (error) {
            console.warn("Error : ", error);
        }
    };

    const getDeptUsersList = async (deptId) => {
        try {
            let res = await apiCall({ method: 'get', url: `get-dept-users-list/${deptId}` });
            setDeptUserList(res?.deptUsersList);
        } catch (error) {
            console.warn("Error : ", error)
        }
    }

    useEffect(() => {
        getDropDownList();
    }, []);

    const [userInput, setUserInput] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    const handleUserInputChange = (e) => setUserInput(e.target.value);

    const handleUserInputKeyDown = (e) => {
        const inputVal = userInput.trim();
        if ((e.key === 'Enter' || e.key === ',') && inputVal !== '') {
            e.preventDefault();
            const match = (userOptions || []).find(
                u => u.emp_name.toLowerCase() === inputVal.toLowerCase()
            );
            if (match && !selectedUsers.find(u => u.emp_id === match.emp_id)) {
                const updated = [...selectedUsers, match];
                setSelectedUsers(updated);
                formik.setFieldValue('userId', updated.map(u => u.emp_id)); // store emp_id
                setUserInput('');
            }
        }
    };

    const handleSelectUser = (user) => {
        if (!selectedUsers.find(u => u.emp_id === user.emp_id)) {
            const updated = [...selectedUsers, user];
            setSelectedUsers(updated);
            formik.setFieldValue('userId', updated.map(u => u.emp_id)); // store emp_id
            setUserInput('');
        }
    };

    const removeUserTag = (index) => {
        const updated = [...selectedUsers];
        updated.splice(index, 1);
        setSelectedUsers(updated);
        formik.setFieldValue('userId', updated.map(u => u.emp_id)); // keep emp_id list
    };

    return (
        <form onSubmit={formik.handleSubmit} className={Style.modal_body}>
            {/* popupStatus */}
            <LeftSidePopupLayout popupStatus={true} className={Style.departmentChangeModal}>
                <div className={Style.modal_header}>
                    <h6>Bulk Employee Data Change</h6>
                    <div onClick={() => popupClosed()} className={Style.close_btn}><XCloseIcon /></div>
                </div>

                <div className={Style.modal_body}>
                    <div className={Style.dcmb__card}>
                        <div className={Style.dcmb__cardBody}>
                            <div className={Style.dcmb__cardBody_formArea}>
                                {/* User Search Field */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>User <span>*</span></label>
                                    <div className={Style.tag_wrapper}>
                                        <div className={Style.tag_list}>
                                            {selectedUsers.map((user, idx) => (
                                                <span className={Style.tag} key={user.emp_id}>
                                                    <span className={Style.tag_text}>{user.emp_name}</span>
                                                    <button
                                                        type="button"
                                                        className={Style.tag_remove}
                                                        onClick={() => removeUserTag(idx)}
                                                    >
                                                        <XCloseIcon />
                                                    </button>
                                                </span>
                                            ))}
                                            <input
                                                type="text"
                                                className={Style.tag_input}
                                                placeholder="Type to search users..."
                                                value={userInput}
                                                onChange={handleUserInputChange}
                                                onKeyDown={handleUserInputKeyDown}
                                            />
                                        </div>
                                        {userInput.length > 0 && (
                                            <ul className={Style.suggestion_list}>
                                                {userOptions
                                                    .filter(u =>
                                                        u.emp_name.toLowerCase().includes(userInput.toLowerCase()) &&
                                                        !selectedUsers.find(s => s.emp_id === u.emp_id)
                                                    )
                                                    .map(u => (
                                                        <li key={u.emp_id} className={Style.suggestion_item} onClick={() => handleSelectUser(u)}>
                                                            {u.emp_name}
                                                        </li>
                                                    ))
                                                    .concat(
                                                        userOptions.filter(u =>
                                                            u.emp_name.toLowerCase().includes(userInput.toLowerCase()) &&
                                                            !selectedUsers.find(s => s.emp_id === u.emp_id)
                                                        ).length === 0
                                                            ? [<li key="no-user" className={Style.suggestion_item}>No user found</li>]
                                                            : []
                                                    )}
                                            </ul>
                                        )}
                                    </div>
                                    {formik.touched.userId && formik.errors.userId && (
                                        <div className={Style.error}>{formik.errors.userId}</div>
                                    )}
                                </div>

                                {/* Department */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Department</label>
                                    <select
                                        name="department"
                                        onChange={(e) => {
                                            getDeptUsersList(e.target.value)
                                            formik.setFieldValue("department", e.target.value)
                                        }}
                                        value={formik.values.department}
                                    >
                                        <option value="" selected disabled>Department</option>
                                        {departmentList.map((el, idx) => (
                                            <option key={idx} value={el.id}>{el.title}</option>
                                        ))}
                                    </select>
                                    {formik.touched.department && formik.errors.department && (
                                        <div className={Style.error}>{formik.errors.department}</div>
                                    )}
                                </div>

                                {/* Designation */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Designation</label>
                                    <input
                                        type={'text'}
                                        name={'designation'}
                                        onChange={formik.handleChange}
                                        value={formik.values.designation}
                                        placeholder='Designation'
                                    />
                                    {formik.touched.designation && formik.errors.designation && (
                                        <div className={Style.error}>{formik.errors.designation}</div>
                                    )}
                                </div>

                                {/* Employee Type */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Employee Type</label>
                                    <select
                                        name="employee_type"
                                        onChange={formik.handleChange}
                                        value={formik.values.employee_type}
                                    >
                                        <option value="" disabled>Employee Type</option>
                                        <option value="Probation">Probation</option>
                                        <option value="Permanent">Permanent</option>
                                        <option value="Internship">Internship</option>
                                    </select>
                                    {formik.touched.employee_type && formik.errors.employee_type && (
                                        <div className={Style.error}>{formik.errors.employee_type}</div>
                                    )}
                                </div>

                                {/* Reporting Authority */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Reporting Authority</label>
                                    <select
                                        name="reporting_authority"
                                        onChange={formik.handleChange}
                                        value={formik.values.reporting_authority}
                                    >
                                        <option value="" disabled>Reporting Authority</option>
                                        {deptUserList.map((el, idx) => (
                                            <option key={idx} value={el.employee_id}>{el.employee_name}</option>
                                        ))}
                                    </select>
                                    {formik.touched.reporting_authority && formik.errors.reporting_authority && (
                                        <div className={Style.error}>{formik.errors.reporting_authority}</div>
                                    )}
                                </div>

                                {/* Shift */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Shift</label>
                                    <select
                                        name="shift"
                                        onChange={formik.handleChange}
                                        value={formik.values.shift}
                                    >
                                        <option value="" disabled>Shift</option>
                                        {Object.entries(shiftList).map(([key, val], i) => (
                                            <option key={i} value={key}>{val}</option>
                                        ))}
                                    </select>
                                    {formik.touched.shift && formik.errors.shift && (
                                        <div className={Style.error}>{formik.errors.shift}</div>
                                    )}
                                </div>

                                {/* Reason */}
                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    <label>Reason <span>*</span></label>
                                    <textarea name="reason"
                                        onChange={formik.handleChange}
                                        value={formik.values.reason}></textarea>
                                    {formik.touched.reason && formik.errors.reason && (
                                        <div className={Style.error}>{formik.errors.reason}</div>
                                    )}
                                </div>

                                <div className={Style.dcmb__cardBody_formArea_feild}>
                                    {formik.errors.atLeastOne && (
                                        <div className={Style.error}>
                                            {formik.errors.atLeastOne}
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>
                    </div>
                </div>

                <div className={Style.dcmb__cardFoot}>
                    <div className={Style.dcmb__cancel} onClick={onClose}>Cancel</div>
                    <button type="submit" className={Style.dcmb__submit}>Submit</button>
                </div>
            </LeftSidePopupLayout>
        </form>
    )
}

export default EmployeeUpdateBulk
